import userflow from 'userflow.js'
import * as Sentry from '@sentry/core'
import type { ManualSurveyKey } from '../engagement/survey-keys'

export function triggerFlow(flowId: string) {
  if (!process.env.USER_FLOW_TOKEN || !flowId) {
    return
  }
  Promise.resolve(userflow.start(flowId)).catch((error) => {
    console.error('Error triggering userflow survey', error)
    Sentry.captureException(error)
  })
}

/** The Userflow flow each survey trigger opened before the in-house surveys (#25349). */
export const USERFLOW_FLOW_BY_SURVEY_KEY: Record<ManualSurveyKey, string> = {
  'report-feedback': '49efb94f-9fca-4d57-b8d1-e796a8f62a0a',
  'swap-products-feedback': '25ce3f5b-0f01-4d68-a339-7656d2b03562',
  'operations-trial-started': '85160fab-9bea-4df6-8220-8bc23b77b835',
  'crm-trial-cancelled': 'f1692e79-545a-4055-b9dd-a4d7b8130114',
  'checkout-single-user': '77b4c54b-5274-43c9-a177-11aa30eff09b',
  'checkout-team': '63ebdc50-2e8a-43da-b5ef-220dd0996287',
  'checkout-team-crm': '8b7baf8d-42a6-4e1a-9562-1038b9363224'
}

/** Opens the in-house survey with this key. Returns false when there is none to open. */
export type SurveyTriggerHandler = (key: ManualSurveyKey) => boolean

let surveyTriggerHandler: SurveyTriggerHandler | null = null

/**
 * The app registers this while `inHouseSurveys` is on (SurveyCenter). Packages cannot read the
 * flag or the app's survey state, so they call `triggerSurvey` and the handler decides. Returns
 * the function that unregisters it.
 */
export function registerSurveyTrigger(handler: SurveyTriggerHandler): () => void {
  surveyTriggerHandler = handler
  return () => {
    if (surveyTriggerHandler === handler) surveyTriggerHandler = null
  }
}

/**
 * Opens the survey for this trigger. With `inHouseSurveys` on and a live manual survey for `key`,
 * that survey opens in the app. Otherwise the trigger's Userflow flow runs, as it did before:
 * flag off, the survey not created yet, or the user not in its audience.
 */
export function triggerSurvey(key: ManualSurveyKey): void {
  let handled = false
  try {
    handled = !!surveyTriggerHandler?.(key)
  } catch (error) {
    Sentry.captureException(error)
  }
  if (!handled) triggerFlow(USERFLOW_FLOW_BY_SURVEY_KEY[key])
}
